import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { FileText, Loader2, AlertTriangle, Mail, ExternalLink, Copy, Check } from 'lucide-react';
import { Card, TableSkeleton } from '../../components/ui';
import {
	EmailWorkflowApi,
	WorkflowDetail,
	WorkflowScope,
	WorkflowTimeline,
	WorkflowAttachment,
	validateProofFile,
} from '../../services/emailWorkflowApi';
import { ProofFileInput } from './ProofFileInput';
import { formatINR } from '../../utils/currencyFormatter';
import { ApprovalBadge, FinanceBadge, TierBadge, facilityLabel } from './badges';

/**
 * Shared detail body — rendered by BOTH the board slide-over and the standalone
 * /:threadId page, so the two never drift.
 *
 * `onChanged` fires after any mutation (approve/finance) so the host (board) can
 * refresh just that row and keep the panel open showing the new state — no blind
 * full-board refetch (see design critique: avoid the mutate-then-refresh race).
 */

// amounts come off the API as strings ("6000.00") — coerce before formatting.
const num = (v: unknown): number | null => {
	if (v == null) return null;
	const n = Number(v);
	return Number.isFinite(n) ? n : null;
};

// "Sanjay Manjhi <sanjay@x.com>" → "Sanjay Manjhi"; falls back to the raw string / email.
const displayName = (addr: string | undefined): string => {
	if (!addr) return '—';
	const m = addr.match(/^\s*"?([^"<]+?)"?\s*<[^>]+>/);
	if (m) return m[1].trim();
	return addr.split(',')[0].trim();
};
// a "to" field can list many recipients — show first + "+N".
const recipientSummary = (to: string | undefined): string => {
	if (!to) return '—';
	const parts = to.split(',').map(s => s.trim()).filter(Boolean);
	if (parts.length === 0) return '—';
	const first = displayName(parts[0]);
	return parts.length > 1 ? `${first} +${parts.length - 1}` : first;
};
// snippets arrive HTML-escaped (&lt; &amp; …) — decode for display (text only, not rendered as HTML).
const decodeEntities = (s: string | undefined): string => {
	if (!s) return '';
	return s
		.replace(/&lt;/g, '<')
		.replace(/&gt;/g, '>')
		.replace(/&quot;/g, '"')
		.replace(/&#39;/g, "'")
		.replace(/&amp;/g, '&');
};

// ISO ts → "26 Jun 2026, 09:30"
const fmtTs = (ts: string | undefined): string => {
	if (!ts) return '';
	const d = new Date(ts);
	if (Number.isNaN(d.getTime())) return ts;
	return d.toLocaleString('en-IN', {
		day: '2-digit',
		month: 'short',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});
};

const AttachmentItem: React.FC<{ att: WorkflowAttachment }> = ({ att }) => {
	const [blobUrl, setBlobUrl] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [err, setErr] = useState<string | null>(null);
	const isImage = /^image\/(png|jpeg|gif|webp)$/.test(att.content_type || '');
	const isPdf = att.content_type === 'application/pdf';
	// Only pdf/image are safe to render/open in-app. Anything else (incl. text/html,
	// svg, unknown) is download-only — never rendered in a blob tab where it could run.
	const renderable = isImage || isPdf;
	const safeType = renderable ? att.content_type : undefined; // undefined → octet-stream (download)

	useEffect(() => () => {
		if (blobUrl) URL.revokeObjectURL(blobUrl);
	}, [blobUrl]);

	// Fetch (with auth) once, reuse the blob URL for both inline preview and open-in-tab.
	const ensureBlob = async (): Promise<string | null> => {
		if (blobUrl) return blobUrl;
		setLoading(true);
		setErr(null);
		try {
			const url = await EmailWorkflowApi.fetchAttachmentBlobUrl(att.view_url, safeType);
			setBlobUrl(url);
			return url;
		} catch (e) {
			setErr((e as Error).message || 'Could not load file');
			return null;
		} finally {
			setLoading(false);
		}
	};

	const openInNewTab = async () => {
		// Guard: only ever open renderable types in a tab. Non-renderable blobs are
		// octet-stream (download), so opening them would just download — but we don't
		// offer the button for them anyway.
		if (!renderable) return;
		const url = await ensureBlob();
		if (url) window.open(url, '_blank', 'noopener,noreferrer');
	};

	return (
		<div className="border border-gray-200 rounded-md p-3">
			<div className="flex items-center gap-2">
				<FileText className="h-4 w-4 text-gray-400 shrink-0" />
				<span className="text-sm text-gray-800 truncate flex-1">{att.filename}</span>
				<span className="text-xs text-gray-400 hidden sm:inline">{att.content_type}</span>
				{renderable ? (
					<>
						<button
							type="button"
							onClick={ensureBlob}
							className="text-xs text-primary underline disabled:opacity-50"
							disabled={loading}
						>
							{loading ? 'Loading…' : blobUrl ? 'Loaded' : 'Preview'}
						</button>
						<button
							type="button"
							onClick={openInNewTab}
							className="text-xs text-primary underline disabled:opacity-50 inline-flex items-center gap-0.5"
							disabled={loading}
							title="Open in a new tab"
						>
							<ExternalLink className="h-3 w-3" /> Open
						</button>
					</>
				) : (
					// Untrusted / non-previewable type → download only (never rendered in-app).
					<button
						type="button"
						onClick={async () => {
							const url = await ensureBlob();
							if (!url) return;
							const a = document.createElement('a');
							a.href = url;
							a.download = att.filename || 'attachment';
							a.click();
						}}
						className="text-xs text-primary underline disabled:opacity-50"
						disabled={loading}
					>
						{loading ? 'Loading…' : 'Download'}
					</button>
				)}
			</div>
			{err && <p className="text-xs text-red-500 mt-2">{err}</p>}
			{blobUrl && (
				<div className="mt-3">
					{isImage && <img src={blobUrl} alt={att.filename} className="max-h-96 rounded border" />}
					{isPdf && <embed src={blobUrl} type="application/pdf" className="w-full h-80 rounded border" />}
					{!isImage && !isPdf && (
						<a href={blobUrl} download={att.filename} className="text-sm text-primary underline">
							Download {att.filename}
						</a>
					)}
				</div>
			)}
		</div>
	);
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
	<div>
		<div className="text-xs text-gray-400">{label}</div>
		<div className="text-sm text-gray-900">{children}</div>
	</div>
);

// Copyable thread id — the key used in every API call for this request.
const ThreadIdChip: React.FC<{ threadId: string }> = ({ threadId }) => {
	const [copied, setCopied] = useState(false);
	const copy = async () => {
		try {
			await navigator.clipboard.writeText(threadId);
			setCopied(true);
			setTimeout(() => setCopied(false), 1500);
		} catch {
			/* clipboard blocked — ignore, the id is still selectable */
		}
	};
	return (
		<button
			type="button"
			onClick={copy}
			title="Copy thread ID"
			className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 font-mono"
		>
			<span className="text-gray-300">ID</span>
			<span className="select-all">{threadId}</span>
			{copied ? <Check className="h-3 w-3 text-primary" /> : <Copy className="h-3 w-3" />}
		</button>
	);
};

/** One circle per approval seat: J → K → A. Green = approved, red = rejected,
 *  amber ring = it's waiting on them now, gray = up next. Renders ONLY when the
 *  backend ships `chain` on the detail (contract v2 §7) — silent until then. */
interface ChainSeat {
	tier: number;
	role: string;
	name: string | null;
	acted: 'approved' | 'rejected' | null;
	acted_at?: string | null;
}
const ApprovalChain: React.FC<{ chain: unknown; currentTier: number | null }> = ({ chain, currentTier }) => {
	if (!Array.isArray(chain) || chain.length === 0) return null;
	const seats = chain as ChainSeat[];
	return (
		<div className="flex items-center gap-1.5 mb-4 flex-wrap" aria-label="Approval chain">
			{seats.map((s, i) => {
				const isCurrent = s.acted == null && s.tier === currentTier;
				const cls =
					s.acted === 'approved'
						? 'bg-green-500 text-white border-green-500'
						: s.acted === 'rejected'
							? 'bg-red-500 text-white border-red-500'
							: isCurrent
								? 'bg-amber-100 text-amber-700 border-amber-400 ring-2 ring-amber-200'
								: 'bg-gray-100 text-gray-400 border-gray-200';
				return (
					<React.Fragment key={`${s.tier}-${i}`}>
						{i > 0 && <span className="text-gray-300 text-xs">→</span>}
						<span
							title={`${s.name ?? s.role} — ${s.acted ?? (isCurrent ? 'waiting on them now' : 'up next')}${s.acted_at ? ` (${s.acted_at})` : ''}`}
							className={`inline-flex h-7 w-7 items-center justify-center rounded-full border text-xs font-semibold ${cls}`}
						>
							{(s.name || s.role || '?').trim().charAt(0).toUpperCase()}
						</span>
					</React.Fragment>
				);
			})}
		</div>
	);
};

export const WorkflowDetailBody: React.FC<{
	threadId: string;
	/** Fires after any mutation with the success/error message — hosts should toast
	 *  it: a successful Approve removes the row from pending queues, unmounting
	 *  this component (and any inline message) immediately. */
	onChanged?: (message?: string) => void;
	/** Finance acts from the Payment Request Approvals page ONLY (owner decision
	 *  2026-07-05) — the requester board/detail render finance read-only. */
	financeActions?: boolean;
}> = ({
	threadId,
	onChanged,
	financeActions = false,
}) => {
	const [detail, setDetail] = useState<WorkflowDetail | null>(null);
	const [timeline, setTimeline] = useState<WorkflowTimeline | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [acting, setActing] = useState(false);
	const [actionMsg, setActionMsg] = useState<string | null>(null);
	const [reloadKey, setReloadKey] = useState(0);
	// Is this request in MY pending queue? view=pending is server-scoped to the
	// viewer's tier (the approval chain lives in the backend DB), so queue
	// membership — not user type — is what makes someone the approver here.
	const [inMyQueue, setInMyQueue] = useState(false);
	// finance uploaded a payment proof in this panel (unlocks Mark paid)
	const [proofUploadedNow, setProofUploadedNow] = useState(false);
	const [lastProofName, setLastProofName] = useState<string | null>(null);
	// Is it MY TURN? The viewer must hold the seat for the request's CURRENT tier
	// (found 2026-07-05: the server lets any later-tier seat holder approve out of
	// turn — e.g. tier2 approving while tier1 hasn't acted. UI enforces the turn
	// from the /me descriptor until the backend fence lands; contract v2 §3.)
	const [scope, setScope] = useState<WorkflowScope | null | 'unavailable'>(null);
	const [showTimeline, setShowTimeline] = useState(false);
	const viewerId = useSelector((s: RootState) => s.auth.user?.id);

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		setError(null);
		Promise.all([
			EmailWorkflowApi.getDetail(threadId),
			EmailWorkflowApi.getTimeline(threadId).catch(() => null), // timeline is best-effort
			// ponytail: one page of 200 covers real queues; paginate if a queue ever exceeds that
			EmailWorkflowApi.getBoard({ view: 'pending', page_size: 200 }).catch(() => null),
			EmailWorkflowApi.getMe().catch(() => 'unavailable' as const), // old backends have no /me
		])
			.then(([d, t, queue, me]) => {
				if (cancelled) return;
				setDetail(d);
				setTimeline(t);
				setInMyQueue(queue?.rows.some(r => r.thread_id === threadId) ?? false);
				setScope(me);
			})
			.catch((e: unknown) => {
				if (!cancelled) setError((e as { message?: string })?.message || 'Could not load this request.');
			})
			.finally(() => !cancelled && setLoading(false));
		return () => {
			cancelled = true;
		};
	}, [threadId, reloadKey]);

	const st = (detail?.state ?? {}) as Record<string, any>;
	const eff = detail?.effective_approval;
	const isApproved = eff?.status === 'approved';
	// Raised it yourself → never approve it, even if it also sits in your pending view
	// (the server folds own raised requests into view=pending for non-super users).
	const isMine = st.created_by != null && String(st.created_by) === String(viewerId ?? '');
	// My turn = I hold the seat for THIS facility at the request's CURRENT tier.
	// No /me on the backend → fall back to the old queue-membership-only gating.
	const myTurn =
		scope === 'unavailable' ||
		(scope != null &&
			scope.approver_at.some(s => s.facility_key === st.facility_key && s.role === st.current_tier_role));
	// A request still needs approving while it sits at an APPROVER tier. Don't use
	// effective_approval_status here: the backend keeps ONE decision row, so after
	// tier1 of a two-tier chain it already reads "approved" while tier2 is still
	// owed (contract v2 §4b). current_tier_role is the field that tells the truth.
	const atApproverTier = st.current_tier_role === 'tier1' || st.current_tier_role === 'tier2';
	const canApprove =
		!!detail && atApproverTier && eff?.status !== 'rejected' && inMyQueue && !isMine && myTurn;

	// The API returns some uploads twice: a RAW record (no content_type) plus a
	// TYPED twin (#219). Hide only that exact signature — never merge two typed
	// records, filename is not identity (two real "invoice.pdf"s stay separate).
	const attachments = React.useMemo(() => {
		const all = detail?.attachments ?? [];
		return all.filter(
			a => a.content_type || !all.some(b => b !== a && b.filename === a.filename && b.content_type)
		);
	}, [detail]);

	// Mark paid needs a payment proof. Heuristic until the server enforces it:
	// a second distinct file beyond the raiser's invoice, or an upload just made
	// here. ponytail: server-side rule is filed as the real fence.
	const hasPaymentProof = proofUploadedNow || attachments.length >= 2;

	const runAction = async (fn: () => Promise<string>) => {
		setActing(true);
		setActionMsg(null);
		try {
			const msg = await fn();
			setActionMsg(msg);
			setReloadKey(k => k + 1); // refetch THIS request only; panel stays open
			onChanged?.(msg); // host reloads its list AND toasts (the row may vanish from it)
		} catch (e) {
			setActionMsg((e as { message?: string })?.message || 'Action failed.');
		} finally {
			setActing(false);
		}
	};

	const doApprove = (decision: 'approved' | 'rejected') =>
		runAction(async () => {
			const res = await EmailWorkflowApi.approve(threadId, decision);
			if (decision === 'rejected') return 'Rejected.';
			return res.routing?.done
				? 'Approved — fully done.'
				: `Approved — moved to tier ${res.routing?.current_tier ?? '—'}.`;
		});

	const doFinance = (finance_status: 'paid' | 'recheck' | 'done') =>
		runAction(async () => {
			await EmailWorkflowApi.finance(threadId, finance_status);
			return `Finance set to ${finance_status}.`;
		});

	if (loading && !detail) return <TableSkeleton />; // refetches keep the panel steady
	if (error)
		return (
			<Card className="text-center py-10">
				<p className="text-red-600 font-medium">{error}</p>
				<button type="button" onClick={() => setReloadKey(k => k + 1)} className="mt-3 text-sm text-primary underline">
					Try again
				</button>
			</Card>
		);
	if (!detail) return null;

	const invoiceAmt = num(st.attachment_amount);
	const emailAmt = num(st.email_claimed_amount);

	return (
		<div className={`space-y-4 ${loading ? 'opacity-60 transition-opacity' : 'transition-opacity'}`}>
			<div>
				<p className="text-sm text-gray-500">{st.gist}</p>
				<div className="flex flex-wrap gap-2 mt-3">
					{eff && <ApprovalBadge status={eff.status} source={eff.source} />}
					{st.finance_status && <FinanceBadge status={st.finance_status} />}
					<TierBadge role={st.current_tier_role ?? null} effectiveApproved={isApproved} financeStatus={st.finance_status} />
				</div>
				<div className="mt-2">
					<ThreadIdChip threadId={(st.provider_thread_id as string) || threadId} />
					{/* fallback-key note (#197): emails were served from a different thread */}
					{timeline?.messages_source_thread &&
						timeline.messages_source_thread !== ((st.provider_thread_id as string) || threadId) && (
							<div className="text-xs text-gray-400 mt-0.5">
								emails from thread{' '}
								<span className="font-mono select-all">{timeline.messages_source_thread}</span>
							</div>
						)}
				</div>
			</div>

			{/* amounts + attribution */}
			<Card>
				<div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
					<Field label="Amount (invoice)">{invoiceAmt != null ? formatINR(invoiceAmt) : '—'}</Field>
					<Field label="Amount (email said)">{emailAmt != null ? formatINR(emailAmt) : '—'}</Field>
					<Field label="City">{st.city || <span className="text-gray-400">Unattributed</span>}</Field>
					<Field label="Facility">{facilityLabel(st.facility_key) || '—'}</Field>
				</div>
				{st.amount_match === 0 && (
					<p className="mt-3 flex items-center gap-1.5 text-sm text-amber-600">
						<AlertTriangle className="h-4 w-4" /> The invoice amount and the email amount don&apos;t match — check
						before approving.
					</p>
				)}
			</Card>

			{/* attachments + decision in ONE row: invoice on the left, Approve/Reject
			    at the right end (owner layout 2026-07-05) — check, then act, no scroll */}
			<Card>
				<div className="flex flex-col sm:flex-row sm:items-start gap-3">
					<div className="flex-1 min-w-0">
						<h2 className="text-sm font-semibold text-gray-700 mb-3">
							Attachments{attachments.length > 0 && ` (${attachments.length})`}
						</h2>
						{attachments.length === 0 ? (
							<p className="text-sm text-gray-400">No files on this thread.</p>
						) : (
							<div className="space-y-2">
								{attachments.map(a => (
									<AttachmentItem key={a.id} att={a} />
								))}
							</div>
						)}
					</div>
					{canApprove && (
						<div className="flex sm:flex-col gap-2 shrink-0 sm:pt-8">
							<button
								type="button"
								disabled={acting || loading}
								onClick={() => doApprove('approved')}
								className="px-4 py-1.5 rounded-md bg-primary text-white text-sm font-medium disabled:opacity-50 inline-flex items-center justify-center gap-1"
							>
								{acting && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Approve
							</button>
							<button
								type="button"
								disabled={acting || loading}
								onClick={() => doApprove('rejected')}
								className="px-4 py-1.5 rounded-md border border-red-300 text-red-600 text-sm font-medium disabled:opacity-50"
							>
								Reject
							</button>
						</div>
					)}
				</div>
			</Card>

			{/* approval — decision info (the buttons live next to the invoice above) */}
			<Card>
				<h2 className="text-sm font-semibold text-gray-700 mb-3">Approval</h2>
				<ApprovalChain chain={st.chain} currentTier={st.current_tier ?? null} />
				{detail.decision ? (
					<div className="grid grid-cols-2 gap-4">
						<Field label="Approved by">{detail.decision.approved_by_name || '—'}</Field>
						<Field label="When">{detail.decision.approved_at || '—'}</Field>
						<Field label="Evidence / note">{detail.decision.approval_evidence || '—'}</Field>
						<Field label="Source">{detail.decision.approval_source || '—'}</Field>
					</div>
				) : isApproved ? (
					<p className="text-sm text-gray-600">Approved in email ({eff?.source}). No dashboard decision recorded.</p>
				) : (
					<p className="text-sm text-gray-500">No one has acted yet.</p>
				)}
				{!canApprove && (
					<p className="text-xs text-gray-400 mt-4">
						{atApproverTier
							? isMine
								? 'You raised this request — its current approver will act on it.'
								: 'This request is not at your tier — only its current approver can act.'
							: 'No approval action available.'}
					</p>
				)}
			</Card>

			{/* finance */}
			<Card>
				<h2 className="text-sm font-semibold text-gray-700 mb-3">Finance</h2>
				{!financeActions ? (
					<p className="text-xs text-gray-400">
						Payments are recorded by the finance team on the Payment Request Approvals
						page — the status badge above updates automatically.
					</p>
				) : isApproved ? (
					<>
						<div className="flex flex-wrap items-center gap-2">
							<button
								type="button"
								disabled={acting || loading || !hasPaymentProof}
								title={hasPaymentProof ? undefined : 'Attach the payment proof (UTR / receipt) first'}
								onClick={() => doFinance('paid')}
								className="px-3 py-1.5 rounded-md bg-blue-600 text-white text-sm disabled:opacity-50"
							>
								Mark paid
							</button>
							<button type="button" disabled={acting || loading} onClick={() => doFinance('done')} className="px-3 py-1.5 rounded-md bg-green-600 text-white text-sm disabled:opacity-50">
								Mark done
							</button>
							<button type="button" disabled={acting || loading} onClick={() => doFinance('recheck')} className="px-3 py-1.5 rounded-md border border-amber-300 text-amber-700 text-sm disabled:opacity-50">
								Flag recheck
							</button>
							{!hasPaymentProof && (
								<span className="text-xs text-amber-600">Attach the payment proof below to unlock Mark paid.</span>
							)}
						</div>
						<div className="mt-4">
							<label className="text-xs text-gray-500 block mb-1">Attach payment proof (UTR / receipt)</label>
							<ProofFileInput
								disabled={acting}
								fileName={lastProofName}
								onFile={f => {
									if (!f) return;
									const err = validateProofFile(f);
									if (err) {
										setActionMsg(err);
										return;
									}
									void runAction(async () => {
										await EmailWorkflowApi.uploadProofFile(threadId, f);
										setLastProofName(f.name);
										setProofUploadedNow(true);
										return `Payment proof "${f.name}" uploaded.`;
									});
								}}
							/>
							<p className="text-xs text-gray-400 mt-1">PDF, DOCX, JPG, PNG or WebP — up to 15 MB. It will appear under Attachments.</p>
						</div>
					</>
				) : (
					<p className="text-xs text-gray-400">Finance actions unlock after the request is approved.</p>
				)}
			</Card>

			{actionMsg && (
				<div className="text-sm px-3 py-2 rounded-md bg-gray-50 border border-gray-200 text-gray-700">{actionMsg}</div>
			)}

			{/* email timeline — "conversation flow": collapsed by default (it's the tallest
			    section; matters in the accordion row), header shows the message count */}
			<Card>
				<button
					type="button"
					onClick={() => setShowTimeline(s => !s)}
					aria-expanded={showTimeline}
					className="w-full text-left text-xs font-semibold tracking-wide text-gray-500 uppercase flex items-center gap-1.5"
				>
					<Mail className="h-4 w-4 text-gray-400" /> Conversation flow
					{timeline && timeline.message_count > 0 && (
						<span className="text-gray-400 normal-case tracking-normal">· {timeline.message_count} messages</span>
					)}
					<span className="ml-auto text-gray-400 normal-case tracking-normal">
						{showTimeline ? 'Hide' : 'Show'}
					</span>
				</button>
				{!showTimeline ? null : !timeline || timeline.events.length === 0 ? (
					<p className="text-sm text-gray-400 mt-4">No email history for this request yet.</p>
				) : (
					<ol className="mt-4 relative space-y-5 before:absolute before:left-[5px] before:top-1 before:bottom-1 before:w-px before:bg-gray-200">
						{timeline.events.map((ev, i) => (
							<li key={i} className="relative pl-6">
								{/* dot on the rail */}
								<span className="absolute left-0 top-1.5 h-[11px] w-[11px] rounded-full bg-white border-2 border-primary" />
								{/* From → To + date */}
								<div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 text-sm">
									<span className="font-semibold text-gray-900">{displayName(ev.from)}</span>
									<span className="text-gray-400">→</span>
									<span className="text-gray-600">{recipientSummary(ev.to)}</span>
									{ev.ts && <span className="text-xs text-gray-400 ml-auto">{fmtTs(ev.ts)}</span>}
								</div>
								{ev.subject && <div className="text-sm text-gray-700 mt-0.5">{ev.subject}</div>}
								{(ev.snippet || ev.body_text) && (
									<p className="text-sm text-gray-500 mt-1 whitespace-pre-wrap line-clamp-4">
										{decodeEntities(ev.snippet || ev.body_text)}
									</p>
								)}
								{Array.isArray(ev.attachments) && ev.attachments.length > 0 && (
									<div className="mt-1 text-xs text-gray-400 inline-flex items-center gap-1">
										<FileText className="h-3 w-3" /> {ev.attachments.length} attachment
										{ev.attachments.length > 1 ? 's' : ''}
									</div>
								)}
							</li>
						))}
					</ol>
				)}
			</Card>
		</div>
	);
};

export default WorkflowDetailBody;
